import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiSearch } from 'react-icons/fi';
import { wooAPI } from '../lib/woocommerce';
import { formatPrice } from '../lib/cart';

export default function SearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setHasSearched(false);
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
      }
    };
  }, [isOpen]);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    
    try {
      // Use WooCommerce search API
      const searchResults = await wooAPI.searchProducts(searchQuery, { per_page: 8 });
      setResults(searchResults || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear previous timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    // Debounce search with 500ms delay
    window.searchTimeout = setTimeout(() => {
      handleSearch(value);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(query);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-start justify-center p-4 pt-20">
        <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center p-6 border-b border-gray-800">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Search for dresses, jewelry, or accessories..."
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 text-gray-400 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
                <p className="text-gray-400 mt-2">Searching...</p>
              </div>
            )}

            {!isLoading && hasSearched && results.length === 0 && (
              <div className="p-6 text-center">
                <FiSearch size={48} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-400 mb-2">No results found</h3>
                <p className="text-gray-500 text-sm">
                  Try searching with different keywords or browse our categories
                </p>
              </div>
            )}

            {!isLoading && results.length > 0 && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Search Results ({results.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  {results.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.slug || product.id}`}
                      onClick={onClose}
                      className="flex space-x-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={product.images?.[0]?.src || '/placeholder-image.jpg'}
                          alt={product.name}
                          className="w-20 h-20 object-cover rounded-md"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-400 mt-1">
                          {product.categories?.[0]?.name || 'Product'}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm text-pink-500 font-semibold">
                            {formatPrice(product.price)}
                          </span>
                          {product.sale_price && (
                            <span className="text-xs text-gray-500 line-through">
                              {formatPrice(product.regular_price)}
                            </span>
                          )}
                        </div>
                        {product.stock_status === 'outofstock' && (
                          <span className="text-xs text-red-400">Out of Stock</span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
                
                {results.length >= 8 && (
                  <div className="mt-6 text-center">
                    <Link
                      to={`/search?q=${encodeURIComponent(query)}`}
                      onClick={onClose}
                      className="text-pink-500 hover:text-pink-400 text-sm"
                    >
                      View all results for "{query}"
                    </Link>
                  </div>
                )}
              </div>
            )}

            {!isLoading && !hasSearched && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Popular Searches</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Ballerina Dresses',
                    'Tutu Skirts',
                    'Dance Jewelry',
                    'Ballet Shoes',
                    'Performance Costumes',
                    'Dance Accessories',
                  ].map((term) => (
                    <button
                      key={term}
                      onClick={() => {
                        setQuery(term);
                        handleSearch(term);
                      }}
                      className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm hover:bg-gray-700 hover:text-white transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
